import { useState } from 'react'

import {
  Button,
  Crunch,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ShareActionType,
  Spinner,
} from '@dc-shared-web/components'
import {
  SharedDocumentInfo,
  useGetSharedDocumentInfo,
  useRevokeSharedDocument,
} from '@dc-shared-web/hooks/api'
import { cn } from '@dc-shared-web/lib'
import {
  faUserGroup,
  faChevronDown,
} from '@document-crunch/pro-regular-svg-icons'

export function SharedUsersList({
  document,
  onActionSuccess,
  onActionError,
}: {
  document: Crunch
  onActionSuccess?: (
    actionType: ShareActionType,
    affectedEmailList: string[]
  ) => void
  onActionError?: (actionType: ShareActionType) => void
}) {
  const [opens, setOpens] = useState<boolean[]>([])

  const {
    data: shareInfoData,
    isLoading: shareInfoLoading,
    refetch: refetchShares,
  } = useGetSharedDocumentInfo({
    uuid: document.id,
    onSuccess: (data) => {
      setOpens(Object.keys(data.shares).map(() => false))
    },
  })
  const { mutate: revokeShareMutate, isLoading: revokeIsLoading } =
    useRevokeSharedDocument({
      documentId: document.id,
      documentName: document.name,
      onSuccess: (email?: string) => {
        refetchShares()
        onActionSuccess && email && onActionSuccess('SHARE_REVOKE', [email])
      },
      onError: () => {
        onActionError && onActionError('SHARE_REVOKE')
      },
    })

  const updateOpens = (index: number, open: boolean) => {
    setOpens((prev) => prev.map((_, i) => (i === index ? open : false)))
  }

  const onRevoke = (user: SharedDocumentInfo, index: number) => {
    revokeShareMutate({ id: user.id, email: user.email })
    updateOpens(index, false)
  }

  const userList: SharedDocumentInfo[] = shareInfoData?.shares
    ? Object.keys(shareInfoData?.shares).map((key) => {
        return { ...shareInfoData?.shares?.[key][0], shareLinkId: key }
      })
    : []

  return (
    <>
      <h2 className='tw-text-sm tw-text tw-font-medium tw-text-neutral-700'>
        People with access
      </h2>
      <div
        className='tw-flex tw-items-center tw-gap-1 tw--mt-3 tw-text-base'
        data-testid='shared-users-team-members'
      >
        <Icon icon={faUserGroup} size='xs' className='tw-text-neutral-700' />
        <p className='tw-text-2xs tw-font-medium tw-text-neutral-700'>
          Any member of{' '}
          <strong className='tw-font-semibold'>{document.team}</strong> can
          access this crunch
        </p>
      </div>
      {userList && userList.length > 0 && (
        <div
          className={cn(
            'tw-flex tw-flex-col tw-gap-4 tw-max-h-40 tw-overflow-auto tw-py-2 tw-relative',
            userList?.length > 3 && 'tw-border-t tw-border-neutral-100',
            revokeIsLoading && 'tw-pointer-events-none tw-overflow-hidden'
          )}
          data-testid='shared-users-list-item'
        >
          {(revokeIsLoading || shareInfoLoading) && (
            <div className='tw-absolute tw-w-full tw-h-full tw-bg-white/70'>
              <div className='tw-flex tw-items-center tw-justify-center tw-h-full'>
                <Spinner />
              </div>
            </div>
          )}
          {userList.map(
            (user, index) =>
              !user.is_revoked && (
                <div
                  key={user.id}
                  className='tw-flex tw-items-center tw-justify-between'
                >
                  <div className='tw-flex tw-items-center tw-gap-4'>
                    <div className='tw-bg-blue-100 tw-text-blue-600 tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-capitalize tw-text-base'>
                      {user.email?.charAt(0) || ''}
                    </div>
                    <div className='tw-flex tw-flex-col'>
                      <p className='tw-text-sm tw-font-medium tw-text-neutral-700 tw-max-w-[225px] tw-truncate'>
                        {user.shared_with || user.email || ''}
                      </p>
                      <p className='tw-text-sm tw-text-neutral-700 tw-max-w-[225px] tw-truncate'>
                        {user.email || ''}
                      </p>
                    </div>
                  </div>
                  <Popover
                    open={opens[index]}
                    onOpenChange={(open) => updateOpens(index, open)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        size='sm'
                        variant='outline'
                        className='tw-text-neutral-700 tw-flex tw-whitespace-nowrap tw-gap-2 tw-text-sm tw-w-[125px] tw-rounded-lg tw-py-2 tw-h-auto tw-justify-between tw-mr-1'
                        tabIndex={3}
                      >
                        {user.is_accepted ? 'Has access' : 'Pending'}
                        <Icon
                          icon={faChevronDown}
                          className='tw-h-4 tw-w-4 tw-opacity-50'
                        />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className='tw-px-0 tw-py-1 tw-w-[130px]'
                      align='start'
                      sideOffset={0}
                      data-testid='shared-users-popover'
                    >
                      <Button
                        size='sm'
                        onClick={() => onRevoke(user, index)}
                        variant='outline'
                        className='tw-text-neutral-700 tw-whitespace-nowrap tw-border-0 tw-w-full tw-text-sm tw-py-2 tw-h-auto tw-font-normal'
                      >
                        Remove access
                      </Button>
                    </PopoverContent>
                  </Popover>
                </div>
              )
          )}
        </div>
      )}
    </>
  )
}
